import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { toast } from 'react-hot-toast';
import apiClient from '../api/client';
import { Copy } from 'lucide-react';
import { getAllDomainsLookup, getUserDomainAccess, UpdateUserDomainAccess, createAdminUser, updateAdminUser, getDomains } from '../services/masterDataService';
import { selectStyles as sharedSelectStyles } from '../helpers';
import { THEME } from '../theme/themeColors';

// Shared, theme-aware react-select styles (helpers/index.js) — this file used to keep
// an identical local copy. Only addition: the menu is portaled to <body>, so it must
// sit above this modal's overlay (z-index 1060).
const selectStyles = {
  ...sharedSelectStyles,
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const DEFAULT_ROW = { isReadOnly: false, isReadWrite: false, isActive: false };

const EditAdminUserModal = ({ show, onClose, user, roles, onSuccess }) => {
  const [form, setForm] = useState({
    userId: 0,
    szUserName: '',
    szEmail: '',
    userRoleId: null,
  });

  const [updating, setUpdating] = useState(false);

  const [domains, setDomains] = useState([]);
  const [access, setAccess] = useState({}); // { [domainId]: { isReadOnly, isReadWrite, isActive } }
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [plainPassword, setPlainPassword] = useState("");
  const [loadingPassword, setLoadingPassword] = useState(false);

  // Snapshot of the access map as it came from the server, used to diff
  // against on save so we only send rows the admin actually changed.
  const originalAccessRef = useRef({});

  // Populate the base user fields whenever the modal opens for a given user
  useEffect(() => {
    if (show && user && roles?.length > 0) {
      const roleId = user.userRoleId ?? user.UserRoleId ?? null;

      setForm({
        userId: user.szUserId || user.userId || 0,
        szUserName: user.szUserName || '',
        szEmail: user.szEmail || '',
        userRoleId: roleId,
      });
    }
  }, [show, user, roles]);

  // Load all domains + this user's existing mappings, merge into one access map
  useEffect(() => {
    if (!show || !form.userId) return;

    const loadAccess = async () => {
      const currentDomain = sessionStorage.getItem("selectedDomain") || "greenyourbills.com";
      setDomainsLoading(true);
      try {
        const [allDomains, existingMappings] = await Promise.all([
          getAllDomainsLookup(currentDomain),
          getUserDomainAccess(form.userId, currentDomain),
        ]);

        setDomains(allDomains || []);

        const initial = {};
        (allDomains || []).forEach((d) => {
          initial[d.id] = { isReadOnly: false, isReadWrite: false, isActive: false };
        });
        // Overlay the user's existing mappings on top of the default rows
        (existingMappings || []).forEach((m) => {
          initial[m.domainId] = {
            isReadOnly: !!m.isReadOnly,
            isReadWrite: !!m.isReadWrite,
            isActive: !!m.isEnabled,
          };
        });

        setAccess(initial);
        // Deep-copy the snapshot so later mutations to `access` don't affect it
        originalAccessRef.current = JSON.parse(JSON.stringify(initial));
      } catch (err) {
        toast.error("Failed to load domain access");
        setDomains([]);
        setAccess({});
        originalAccessRef.current = {};
      } finally {
        setDomainsLoading(false);
      }
    };

    loadAccess();
  }, [show, form.userId]);

  useEffect(() => {
    setShowPassword(false);
    setPlainPassword("");
    setLoadingPassword(false);
  }, [user?.szUserId]);

  // Enforces "exactly one of ReadOnly/ReadWrite" at the UI level,
  // matching the CK_UserDomainMapping_AccessFlags constraint on the backend.
  const toggleFlag = (domainId, flag) => {
    setAccess((prev) => {
      const row = prev[domainId] || { ...DEFAULT_ROW };
      const updated = { ...row };

      if (flag === 'isActive') {
        const nextActive = !row.isActive;
        updated.isActive = nextActive;

        if (nextActive) {
          if (!row.isReadOnly && !row.isReadWrite) {
            updated.isReadOnly = true;
            updated.isReadWrite = false;
          }
        } else {
          updated.isReadOnly = false;
          updated.isReadWrite = false;
        }
      }
      else if (flag === 'isReadOnly') {
        const next = !row.isReadOnly;
        updated.isReadOnly = next;
        if (next) updated.isReadWrite = false;
      }
      else if (flag === 'isReadWrite') {
        const next = !row.isReadWrite;
        updated.isReadWrite = next;
        if (next) updated.isReadOnly = false;
      }

      return { ...prev, [domainId]: updated };
    });
  };

  const fetchDecryptedPassword = async () => {
    if (!user?.szEncryptedPassword) {
      setPlainPassword("No password available");
      return;
    }

    setLoadingPassword(true);
    try {
      const res = await apiClient.post('/api/users/decrypt-password', {
        encryptedPassword: user.szEncryptedPassword,
        domain: sessionStorage.getItem("selectedDomain") || "greenyourbills.com"
      });

      setPlainPassword(res.data.password || "[Empty]");
    } catch (err) {
      console.error(err);
      setPlainPassword("[Decryption Failed]");
    } finally {
      setLoadingPassword(false);
    }
  };

  const CopyButton = ({ text, title = "Copy" }) => {
    const [isCopied, setIsCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(text);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 5000);
      } catch (err) {
        toast.error("Failed to copy");
      }
    };

    return (
      <button
        type="button"
        className="btn btn-sm p-1"
        style={{ background: 'transparent', border: 'none', color: '#212529' }}
        onClick={handleCopy}
        title={title}
        aria-label={title}
      >
        {isCopied ? (
          <span style={{ color: THEME.primaryHover, fontSize: '14px', fontWeight: 'bold' }}>✓✓</span>
        ) : (
          <Copy size={14} />
        )}
      </button>
    );
  };

  const rowsAreEqual = (a, b) =>
    !!a?.isReadOnly === !!b?.isReadOnly &&
    !!a?.isReadWrite === !!b?.isReadWrite &&
    !!a?.isActive === !!b?.isActive;

  const handleUpdate = async () => {
    if (!form.szUserName?.trim() || !form.szEmail?.trim() || !form.userRoleId) {
      return toast.error("User Name, Email and Role are required");
    }

    const mappings = domains
      .filter((d) => {
        const current = access[d.id] || DEFAULT_ROW;
        const original = originalAccessRef.current[d.id] || DEFAULT_ROW;
        return !rowsAreEqual(current, original);
      })
      .map((d) => {
        const row = access[d.id] || DEFAULT_ROW;

        if (!row.isActive) {
          return {
            domainId: d.id,
            isReadOnly: false,
            isReadWrite: false,
            status: false,
          };
        }

        // default to Read Only if the admin activated a row without picking either flag
        const isReadWrite = !!row.isReadWrite;
        return {
          domainId: d.id,
          isReadOnly: !isReadWrite,
          isReadWrite,
          status: true,
        };
      });

    setUpdating(true);
    try {
      // 1. update profile fields
      await updateAdminUser(form);
      // 2. save domain access, only if something actually changed
      if (mappings.length > 0) {
        await UpdateUserDomainAccess({
          userId: form.userId,
          roleId: form.userRoleId,
          username: sessionStorage.getItem("username") || "System",
          domain: sessionStorage.getItem("selectedDomain") || "greenyourbills.com",
          mappings,
        });
      }

      toast.success("User updated successfully!");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setUpdating(false);
    }
  };

  if (!show) return null;

  const selectedOption = roles?.find(r => String(r.value) === String(form.userRoleId)) || null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1060,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px'
    }} onClick={onClose}>

      <div style={{
        width: '100%', maxWidth: '560px', background: 'white', borderRadius: '10px',
        overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }} onClick={e => e.stopPropagation()}>

        <div style={{ backgroundColor: THEME.primary, padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '48px' }}>
          <div style={{ color: THEME.onPrimary, fontWeight: 700, fontSize: '15px' }}>Edit User</div>
          <button type="button" aria-label="Close" style={{ fontSize: '26px', background: 'none', border: 'none', color: THEME.onPrimary, marginTop: '-2px' }} onClick={onClose}>×</button>
        </div>

        <div style={{ padding: '14px', maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="row g-3">
            <div className="col-12">
              <label className="filter-label">USER NAME <span className="text-danger">*</span></label>
              <div className="d-flex align-items-center gap-1">
                <input
                  type="text"
                  className="form-control form-control-xs"
                  value={form.szUserName}
                  onChange={(e) => setForm({ ...form, szUserName: e.target.value })}
                />
                {form.szUserName?.trim() && (
                  <CopyButton text={form.szUserName} title="Copy User Name" />
                )}
              </div>
            </div>
            <div className="col-12">
              <label className="filter-label">EMAIL <span className="text-danger">*</span></label>
              <input
                type="email"
                className="form-control form-control-xs"
                value={form.szEmail}
                onChange={(e) => setForm({ ...form, szEmail: e.target.value })}
              />
            </div>
            <div className="col-12">
              <label className="filter-label">ROLE <span className="text-danger">*</span></label>
              <Select
                options={roles || []}
                value={selectedOption}
                onChange={(opt) => setForm({ ...form, userRoleId: opt?.value ?? null })}
                placeholder="Select Role..."
                isClearable
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>

            <div className="col-12">
              <label className="filter-label">PASSWORD</label>
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={showPassword}
                  onChange={(e) => {
                    setShowPassword(e.target.checked);
                    if (e.target.checked) fetchDecryptedPassword();
                    else setPlainPassword("");
                  }}
                  id="showPasswordCheck"
                />
                <label className="form-check-label" htmlFor="showPasswordCheck" style={{ fontSize: '13px' }}>
                  Show password
                </label>
              </div>

              {showPassword && (
                <div className="p-1 border rounded bg-light d-flex align-items-center gap-3" style={{ fontSize: '13px', minHeight: '40px' }}>
                  <span style={{ fontFamily: 'monospace', color: THEME.primaryHover, fontWeight: 500, letterSpacing: '0.5px', flex: 1 }}>
                    {loadingPassword ? "Loading..." : plainPassword}
                  </span>
                  {plainPassword && plainPassword !== "[Empty]" && plainPassword !== "No password available" && (
                    <CopyButton text={plainPassword} title="Copy Password" />
                  )}
                </div>
              )}
            </div>
          </div>

          <hr className="my-3" />

          <label className="filter-label mb-2">DOMAIN ACCESS</label>
          {domainsLoading ? (
            <div className="text-center text-muted py-3" style={{ fontSize: '12px' }}>Loading domain access...</div>
          ) : (
            <table className="table table-sm align-middle mb-0" style={{ fontSize: '12px' }}>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th className="text-center" style={{ width: '90px' }}>Is Read Only</th>
                  <th className="text-center" style={{ width: '90px' }}>Read&amp;Write</th>
                  <th className="text-center" style={{ width: '70px' }}>Is Active</th>
                </tr>
              </thead>
              <tbody>
                {domains.map((d) => {
                  const row = access[d.id] || DEFAULT_ROW;
                  return (
                    <tr key={d.id}>
                      <td>{d.text}</td>
                      <td className="text-center">
                        <input type="checkbox" checked={row.isReadOnly} disabled={!row.isActive} onChange={() => toggleFlag(d.id, 'isReadOnly')} />
                      </td>
                      <td className="text-center">
                        <input type="checkbox" checked={row.isReadWrite} disabled={!row.isActive} onChange={() => toggleFlag(d.id, 'isReadWrite')} />
                      </td>
                      <td className="text-center">
                        <input type="checkbox" checked={row.isActive} onChange={() => toggleFlag(d.id, 'isActive')} />
                      </td>
                    </tr>
                  );
                })}
                {!domains.length && (
                  <tr><td colSpan={4} className="text-center text-muted py-3">No domains found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Compact Footer */}
        <div style={{ padding: '8px 14px', borderTop: '1px solid #e9ecef', display: 'flex', justifyContent: 'flex-end', background: '#fafafa' }}>
          <button className="btn btn-sm btn-outline-secondary px-3" style={{ fontSize: '12px' }} onClick={onClose}>Cancel</button>
          <button
            className="btn btn-sm btn-theme px-4 ms-2"
            style={{ fontSize: '12px' }}
            onClick={handleUpdate}
            disabled={updating || domainsLoading}
          >
            {updating ? 'Updating...' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAdminUserModal;